import React, { useState } from 'react';
import moment from 'moment/min/moment-with-locales';
import ApplicantButtons from '../shared/applicantButtons';
import { useTranslation } from 'react-i18next';
import { getStarColor } from '../../Helpers/applicantHelper';
import * as applicantService from '../../Services/applicantService';
import { basicDateFormat } from '../../Helpers/dateTimeHelper';
import ApplicantCompetence from './applicantCompetences';
import { UncontrolledTooltip, Button } from 'reactstrap';
import EditJobCompetenceModal from '../../Components/Modals/editJobCompetenceModal';
import { getCreatedByMessage } from '../../Helpers/applicantHelper';
import { isObject } from '../ReactHookFormCustom/FormHelpers';
import EditApplicantModal from '../Modals/editApplicantModal';
import { downloadDocument } from '../../Helpers/applicantHelper';
import { CreatedBy } from '../../Helpers/enums';
import AddDocumentModal from '../Modals/addDocumentModal';
import DocumentRow from './documentRow';
import Swal from 'sweetalert2';
import { deleteDocumentFromTheApplicant } from '../../Services/applicantService';
import useAuthStore from '../../../../stores/authStore';
import { hasAdminRole } from '../../Services/utils';

const ApplicantBasicCard = ({ applicant, onStatusRefresh, updateFeOnly }) => {
  const {
    profilePicture,
    firstName,
    lastName,
    email,
    phoneNumber,
    dateOfBirth,
    address,
    applicantJobCompetences,
    isStarred: starredByProp,
    tenantApplicants,
    id,
    signUpForNewsLetter,
    signUpForSimilarJobs,
    linkedInUrl,
    personalWebsiteUrl,
    createdBy,
    xUrl,
    facebookUrl,
    instagramUrl,
    gitHubUrl,
    gitHubProfile,
    documents,
  } = applicant;

  const [isEditCompetenceModalOpen, setIsEditCompetenceModalOpen] = useState(false);
  const [isEditApplicantModalOpen, setIsEditApplicantModalOpen] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const isAdmin = hasAdminRole(user);

  const [isHovered, setIsHovered] = useState(false);

  const { t } = useTranslation();
  const getProfilePic = () => {
    if (isObject(profilePicture)) {
      return profilePicture.url;
    }

    return profilePicture || 'img/user/user-dummy-300x300.png';
  };

  const isStarred = starredByProp !== undefined ? starredByProp : tenantApplicants?.[0]?.isStarred;

  const setStarredApplicant = () => {
    // Optimistically toggle isStarred status
    const currentStarred = isStarred;
    const newStarred = !currentStarred;

    // Update the UI optimistically
    updateFeOnly({ isStarred: newStarred });

    applicantService
      .setStarredApplicant(id)
      .then(() => {})
      .catch((error) => {
        // Revert back to original isStarred value on error
        updateFeOnly({ isStarred: currentStarred });
      });
  };

  const toggleCompetenceModal = () => {
    setIsEditCompetenceModalOpen((isEditCompetenceModalOpen) => !isEditCompetenceModalOpen);
  };

  const toggleAddDocumentModal = () => {
    setIsAddDocumentModalOpen((isAddDocumentModalOpen) => !isAddDocumentModalOpen);
  };

  const saveApplicantCompetence = (values) => {
    values = {
      ...values,
      applicantId: id,
      estimatedExperience: values.competenceWeight?.value,
      competenceId: values.asyncSelectCompetence?.value,
    };

    applicantService
      .addCompetenceToApplicant(values)
      .then((response) => {
        onStatusRefresh();
      })
      .finally((finalData) => {});
  };

  const onEditApplicantClicked = () => {
    setIsEditApplicantModalOpen(true);
  };

  const closeEditApplicantModal = () => {
    setIsEditApplicantModalOpen(false);
  };

  const onDeleteDocument = (id) => {
    Swal.fire({
      title: t('documents.DOCUMENT_REMOVE_QUESTION'),
      showCancelButton: true,
      cancelButtonText: t('common.CANCEL'),
      confirmButtonText: t('common.DELETE'),
      icon: 'warning',
      reverseButtons: true,
      confirmButtonColor: '#dc3545',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteDocumentFromTheApplicant(applicant.id, id).then((result) => {
          onStatusRefresh();
        });
      }
    });
  };

  const cvExists = applicant.cvUrl !== null && applicant.cvUrl !== undefined;

  return (
    <div className="card card-default" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <EditApplicantModal
        isOpen={isEditApplicantModalOpen}
        applicant={applicant}
        closeModal={closeEditApplicantModal}
        refreshApplicant={onStatusRefresh}
      />
      <AddDocumentModal
        isOpen={isAddDocumentModalOpen}
        toggle={toggleAddDocumentModal}
        id={id}
        onStatusRefresh={onStatusRefresh}
      />
      <EditJobCompetenceModal
        isOpen={isEditCompetenceModalOpen}
        modalPurpose={'create'}
        formCompetences={null}
        competence={{}}
        toggle={toggleCompetenceModal}
        saveJobCompetence={saveApplicantCompetence}
        saveJobQuestion={null}
        questionId={null}
        modalIsUsedForApplicantCompetences={true}
        t={t}
      />
      <div className="card-body text-center">
        <div className="py-4 position-relative">
          <img
            id="imgApplicantProfilePic"
            className="img-fluid rounded-circle img-thumbnail thumb96"
            src={getProfilePic()}
            alt="Contact"
          />
          {isHovered && (createdBy === CreatedBy.Recruiter || createdBy === 2) && (
            <em
              id="pencilButton-edit-applicant"
              className="fa-2x fas icon-pencil icon-hover-effect-no-color pointer-on-hover"
              onClick={() => onEditApplicantClicked(applicant, 'edit')}
              style={{
                position: 'absolute',
                right: '1rem',
              }}
            ></em>
          )}
        </div>
        <h3 className="m-0 text-bold">
          <em
            id={'iconForNewApplicant'}
            className="fa fa-star far icon-hover-effect-no-color pr-1"
            onClick={setStarredApplicant}
            style={{ cursor: 'pointer', color: getStarColor(isStarred) }}
          />
          {firstName} {lastName}
        </h3>
        <div className="my-1">
          <h4>{getCreatedByMessage(createdBy, t)}</h4>
        </div>
        <div className="my-1">
          <strong>{t('common.E_MAIL')}:</strong> {email}
        </div>
        {phoneNumber && phoneNumber !== 'undefined' && (
          <div className="my-1">
            <strong>{t('common.PHONE_NUMBER')}:</strong> {phoneNumber}
          </div>
        )}
        {dateOfBirth && dateOfBirth !== '0001-01-01T00:00:00' && (
          <div className="my-1">
            <strong>{t('applicants.D_O_B')}:</strong> {moment.utc(dateOfBirth).format(basicDateFormat)}
          </div>
        )}
        {address && (
          <>
            <div className="my-1">
              <strong>{t('location.LOCATION')}:</strong> {address.city}
            </div>
            <div className="my-1">
              <strong>{t('location.STREET')}:</strong> {address.street}
            </div>
          </>
        )}
        {isAdmin && (
          <>
            <div>
              <input
                className="mr-1"
                id="signUpForSimilarJobsDisabled"
                type="checkbox"
                checked={signUpForSimilarJobs}
                disabled
              />
              <b>{t('applicants.SIGNED_FOR_SIMILAR_JOBS')}</b>
            </div>
            <div>
              <input
                className="mr-1"
                id="signUpForNewsLetterDisabled"
                type="checkbox"
                checked={signUpForNewsLetter}
                disabled
              />
              <b>{t('applicants.SIGNED_FOR_NEWS_LETTER')}</b>
            </div>
          </>
        )}
        <div className="text-center pb-1 pt-2">
          {personalWebsiteUrl && personalWebsiteUrl !== 'undefined' && (
            <a
              href={personalWebsiteUrl}
              id={'iconForPersonalWebsite'}
              className="fa-2x mr-2 fa fa-id-card icon-hover-effect-no-color"
              style={{ textDecoration: 'none' }}
            />
          )}
          {linkedInUrl && linkedInUrl !== 'undefined' && (
            <a
              href={linkedInUrl}
              id={'iconForLinkedIn'}
              className="fa-2x mr-2 fa-brands fa-linkedin icon-hover-effect-no-color"
              style={{ textDecoration: 'none' }}
            />
          )}
          {facebookUrl && facebookUrl !== 'undefined' && (
            <a
              href={facebookUrl}
              id={'iconFacebook'}
              className="fa-2x mr-2 fa-brands fa-facebook icon-hover-effect-no-color"
              style={{ textDecoration: 'none' }}
            />
          )}
          {instagramUrl && instagramUrl !== 'undefined' && (
            <a
              href={instagramUrl}
              id={'iconInstagram'}
              className="fa-2x mr-2 fa-brands fa-instagram icon-hover-effect-no-color"
              style={{ textDecoration: 'none' }}
            />
          )}
          {xUrl && xUrl !== 'undefined' && (
            <a
              href={xUrl}
              id={'iconX'}
              className="fa-2x fa-brands fa-twitter icon-hover-effect-no-color"
              style={{ textDecoration: 'none' }}
            />
          )}
          {gitHubUrl && gitHubUrl !== 'undefined' && (
            <a
              href={gitHubUrl}
              id={'iconGitHub'}
              className="fa-2x fa-brands fa-github icon-hover-effect-no-color"
              style={{ textDecoration: 'none' }}
            />
          )}
        </div>
        <div className="text-center pt-2">
          <ApplicantButtons
            applicant={applicant}
            btnSize={'md'}
            fontSize={'medium'}
            onStatusRefresh={onStatusRefresh}
          />
          {cvExists && (
            <Button
              color="primary"
              onClick={() =>
                downloadDocument(applicant.cvUrl, applicant.firstName + '_' + applicant.lastName + '_CV', t)
              }
            >
              {t('applicants.OPEN_CV')}
            </Button>
          )}
        </div>
      </div>
      <>
        <div className="card-header">
          <div className="card-title">
            <b className="mb-0">Documents:</b>
            {isHovered && (
              <>
                <i
                  id="iconNewDocument"
                  className="fa fa-plus-circle icon-hover-effect float-right fa-lg pt-2"
                  onClick={toggleAddDocumentModal}
                ></i>
                <UncontrolledTooltip placement="left" delay={{ show: 50, hide: 0 }} target="iconNewDocument">
                  New document
                </UncontrolledTooltip>
              </>
            )}
          </div>
        </div>
        {documents && (
          <div>
            <ul className="px-2">
              {documents.map((document, index) => (
                <DocumentRow
                  document={document}
                  key={document.id}
                  onStatusRefresh={onStatusRefresh}
                  onDeleteDocument={onDeleteDocument}
                />
              ))}
            </ul>
          </div>
        )}
      </>
      <>
        <div className="card-header">
          <div className="card-title">
            <b className="mb-0">{t('menu.COMPETENCES')}:</b>
            {isHovered && (
              <>
                <i
                  id="iconNewCompetence"
                  className="fa fa-plus-circle icon-hover-effect float-right fa-lg pt-2"
                  onClick={toggleCompetenceModal}
                ></i>
                <UncontrolledTooltip placement="left" delay={{ show: 50, hide: 0 }} target="iconNewCompetence">
                  {t('competences.NEW')}
                </UncontrolledTooltip>
              </>
            )}
          </div>
        </div>
        <div>
          <ul className="px-2">
            {applicantJobCompetences.map((competence, index) => (
              <ApplicantCompetence
                competence={competence}
                key={competence.id}
                onStatusRefresh={onStatusRefresh}
                actionsEnabled={true}
              />
            ))}
          </ul>
        </div>
      </>
    </div>
  );
};

export default ApplicantBasicCard;
